import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle } from 'lucide-react';

const RequestHistory = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const fetchRequests = async () => {
      try {
        const response = await authFetch('/api/collection-requests');
        if (response.ok) {
          const data = await response.json();
          setRequests(data.requests || []);
        } else {
          toast.error('Failed to load request history');
        }
      } catch (error) {
        toast.error('Network error');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [navigate]);

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-6">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold tracking-tight text-primary">Request History</h1>
          <p className="text-muted-foreground mt-1">View your past collection requests</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <AlertCircle className="text-muted-foreground" size={32} />
                </div>
              </div>
              <h3 className="text-lg font-medium mb-2">No Requests Yet</h3>
              <p className="text-muted-foreground mb-6">
                You haven't submitted any collection requests yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="requests-list">
            {requests.map((request) => (
              <Card key={request._id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {request.request_data?.pet_name || 'Unknown Pet'}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {request.request_data?.pet_type || 'N/A'} • {request.request_data?.breed || 'N/A'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {request.urgent && (
                        <Badge variant="destructive">Urgent</Badge>
                      )}
                      <Badge className="bg-success text-success-foreground">
                        <CheckCircle size={14} className="mr-1" />
                        Submitted
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Client</p>
                      <p className="font-medium">{request.request_data?.client_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Contact</p>
                      <p className="font-medium">{request.request_data?.phone_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Submitted</p>
                      <p className="font-medium">
                        {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {request.pet_record_id && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-muted-foreground italic">
                        In loving memory of a Pure Soul
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RequestHistory;
